import React, { useState, useEffect } from "react";
import { Stepper, Step, StepLabel, Button, Typography, Box, Link } from "@mui/material";
import VideoUpload from "./components/VideoUpload";
import TimeRangeSelector from "./components/TimeRangeSelector";
import DrawRectangles from "./components/DrawRectangles";
import ProcessVideo from "./components/ProcessVideo";
import MouserLabsHeader from "./components/MouserLabsHeader";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import "./App.css";
import UpdateRate from "./components/UpdateRate";
import useVideoFramerate from "./hooks/useVideoFramerate";

const darkTheme = createTheme({
    palette: {
        mode: "dark",
    },
});

const steps = ["Select Video", "Time Range", "Define Regions", "Options", "Review & Extract"];

function App() {
    const [activeStep, setActiveStep] = useState(0);
    const [videoFile, setVideoFile] = useState(null);
    const [startFrame, setStartFrame] = useState(null);
    const [endFrame, setEndFrame] = useState(null);
    const [updateInterval, setUpdateInterval] = useState(1); // Default to 1 fps
    const [rectangles, setRectangles] = useState([]);

    // FPS detection now runs as soon as the video is selected (not waiting for Options tab)
    const { estimatedFps, isDetectingFps, detectionMethod } = useVideoFramerate(videoFile);

    // OCR Engine selection (persisted in localStorage)
    const [ocrEngine, setOcrEngine] = useState(() => {
        return localStorage.getItem('ocrEngine') || 'paddle';
    });

    useEffect(() => {
        localStorage.setItem('ocrEngine', ocrEngine);
    }, [ocrEngine]);

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
    };

    const handleReset = () => {
        setActiveStep(0);
        setVideoFile(null);
        setStartFrame(null);
        setEndFrame(null);
        setRectangles([]);
    };

    const handleVideoSelect = (file) => {
        setVideoFile(file);
        if (file) handleNext();
    };

    // New merged handler for the single "Time Range" step
    const handleSetTimeRange = (start, end) => {
        setStartFrame(start);
        setEndFrame(end);
        // Do not auto-advance here — user clicks Confirm in the TimeRangeSelector
    };

    const getStepContent = (step) => {
        switch (step) {
            case 0:
                return <VideoUpload onFileSelect={handleVideoSelect} selectedFiles={videoFile ? [videoFile] : []} />;
            case 1:
                return (
                    <TimeRangeSelector
                        videoFile={videoFile}
                        onSetTimeRange={handleSetTimeRange}
                        onConfirm={handleNext}
                    />
                );
            case 2:
                return (
                    <DrawRectangles
                        currentRectangles={rectangles}
                        videoFile={videoFile}
                        onRectanglesDrawn={setRectangles}
                        startFrame={startFrame}
                        endFrame={endFrame}
                        onContinue={handleNext}
                    />
                );
            case 3:
                return (
                    <UpdateRate
                        updateInterval={updateInterval}
                        onUpdateRate={setUpdateInterval}
                        startFrame={startFrame}
                        endFrame={endFrame}
                        onContinue={handleNext}
                        videoFile={videoFile}
                        estimatedFps={estimatedFps}
                        isDetectingFps={isDetectingFps}
                        detectionMethod={detectionMethod}
                        ocrEngine={ocrEngine}
                        onOcrEngineChange={setOcrEngine}
                    />
                );
            case 4:
                return (
                    <ProcessVideo 
                        videoFile={videoFile} 
                        startFrame={startFrame} 
                        endFrame={endFrame} 
                        rectangles={rectangles} 
                        updateInterval={updateInterval}
                        ocrEngineType={ocrEngine}
                    />
                );
            default:
                return "Unknown step";
        }
    };

    return (
        <ThemeProvider theme={darkTheme}>
            <CssBaseline />
            <MouserLabsHeader />

            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: '100vh',
                    width: 'auto',
                    padding: '10px 20px 10px 20px',
                    paddingTop: '80px', // Account for fixed header height
                }}
            >
                <Stepper activeStep={activeStep} alternativeLabel>
                    {steps.map((label, index) => {
                        const isClickable = index < activeStep; // only allow going back to completed steps for now
                        return (
                            <Step key={label}>
                                <StepLabel
                                    onClick={() => {
                                        if (isClickable) setActiveStep(index);
                                    }}
                                    sx={{ cursor: isClickable ? "pointer" : "default" }}
                                >
                                    {label}
                                </StepLabel>
                            </Step>
                        );
                    })}
                </Stepper>



                <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                    {activeStep === steps.length ? (
                        <Box sx={{ textAlign: "center", mt: 4 }}>
                            <Typography variant="h6" gutterBottom>All steps completed</Typography>
                            <Button variant="outlined" onClick={handleReset}>
                                Start Over
                            </Button>
                        </Box>
                    ) : (
                        // Scrollable step content using flex instead of fixed calc height.
                        // This prevents the mysterious tall box that was covering content.
                        <Box sx={{ flex: 1, overflow: 'auto', mt: 2, mb: 2, pr: 1 }}>
                            {getStepContent(activeStep)}
                        </Box>
                    )}
                </Box>

                {/* Subtle gray footer with copyright link to Mouser Labs */}
                <Box
                    sx={{
                        py: 1.5,
                        textAlign: 'center',
                        fontSize: '0.8rem',
                        color: 'grey.500',
                    }}
                >
                    <Link
                        href="https://mouserlabs.com"
                        target="_blank"
                        rel="noopener noreferrer"
                        color="inherit"
                        underline="hover"
                    >
                        © Mouser Labs
                    </Link>
                </Box>
            </Box>
        </ThemeProvider>
    );
}

export default App;
